import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, CardBody } from 'reactstrap';
import Icon from '../containers/icon';
import AiChatMessage from './aiChatMessage';

/**
 * AI Chat Input Component
 *
 * A sophisticated chat interface similar to cursor window with:
 * - Message history display
 * - Real-time typing indicators
 * - Conversation threading
 * - Auto-scroll to latest messages
 * - Message timestamps
 */
const defaultOnClear = () => {};
const defaultOnSendMessage = () => {};
const AiChatInput = _ref => {
  let _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$onClear = _ref.onClear,
    onClear = _ref$onClear === void 0 ? defaultOnClear : _ref$onClear,
    placeholder = _ref.placeholder,
    _ref$text = _ref.text,
    text = _ref$text === void 0 ? '' : _ref$text,
    _ref$messages = _ref.messages,
    messages = _ref$messages === void 0 ? [] : _ref$messages,
    _ref$onSendMessage = _ref.onSendMessage,
    onSendMessage = _ref$onSendMessage === void 0 ? defaultOnSendMessage : _ref$onSendMessage,
    _ref$isLoading = _ref.isLoading,
    isLoading = _ref$isLoading === void 0 ? false : _ref$isLoading,
    waitingMessage = _ref.waitingMessage,
    emptyStateMessage = _ref.emptyStateMessage;
  const _useState = useState(text || ''),
    _useState2 = _slicedToArray(_useState, 2),
    currentMessage = _useState2[0],
    setCurrentMessage = _useState2[1];
  const _useState3 = useState(messages),
    _useState4 = _slicedToArray(_useState3, 2),
    localMessages = _useState4[0],
    setLocalMessages = _useState4[1];
  const _useState5 = useState(Date.now().toString()),
    _useState6 = _slicedToArray(_useState5, 1),
    conversationId = _useState6[0];
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastTextRef = useRef(text);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    var _messagesEndRef$curre;
    (_messagesEndRef$curre = messagesEndRef.current) === null || _messagesEndRef$curre === void 0 || _messagesEndRef$curre.scrollIntoView({
      behavior: 'smooth'
    });
  }, []);
  useEffect(() => {
    scrollToBottom();
  }, [localMessages, scrollToBottom]);

  // Sync local messages with prop, but preserve existing conversation
  useEffect(() => {
    if (messages.length > 0) {
      // Only add new error messages, don't replace the entire conversation
      const newErrorMessages = messages.filter(msg => msg.type === 'system' && !localMessages.some(existing => existing.id === msg.id));
      if (newErrorMessages.length > 0) {
        // Remove any waiting messages when error messages are received
        setLocalMessages(prev => {
          const messagesWithoutWaiting = prev.filter(msg => !msg.isWaiting);
          return [...messagesWithoutWaiting, ...newErrorMessages];
        });
      }
    }
  }, [messages, localMessages]);

  // Keep internal state updated when parent clears/changes value
  useEffect(() => {
    if (text !== lastTextRef.current) {
      setCurrentMessage(text || '');
      lastTextRef.current = text;
    }
  }, [text]);

  // Remove waiting messages when loading state changes from true to false
  useEffect(() => {
    if (!isLoading) {
      setLocalMessages(prev => prev.filter(msg => !msg.isWaiting));
    }
  }, [isLoading]);
  const handleSendMessage = () => {
    if (currentMessage.trim()) {
      const userMessage = {
        id: "".concat(conversationId, "-").concat(Date.now(), "-user"),
        content: currentMessage.trim(),
        type: 'user',
        timestamp: new Date().toISOString(),
        conversationId
      };

      // Add user message to local messages
      const updatedMessages = [...localMessages, userMessage];

      // Add waiting response
      const waitingMessageObj = {
        id: "".concat(conversationId, "-").concat(Date.now(), "-waiting"),
        content: waitingMessage,
        type: 'assistant',
        timestamp: new Date().toISOString(),
        isWaiting: true,
        conversationId
      };
      const messagesWithWaiting = [...updatedMessages, waitingMessageObj];

      // Update local messages state
      setLocalMessages(messagesWithWaiting);
      if (onSendMessage) {
        // Call the parent's onSendMessage with message context
        onSendMessage(currentMessage.trim(), {
          conversationId,
          messageId: userMessage.id,
          waitingMessageId: waitingMessageObj.id,
          conversationHistory: updatedMessages
        });
      }
      setCurrentMessage('');
    }
  };
  const handleClear = () => {
    if (onClear) {
      onClear();
      setCurrentMessage('');
    }
  };
  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "ai-chat-input ".concat(className)
  }, /*#__PURE__*/React.createElement(Card, {
    className: "chat-container",
    style: {
      height: '400px',
      display: 'flex',
      flexDirection: 'column'
    }
  }, /*#__PURE__*/React.createElement(CardBody, {
    className: "chat-messages p-3",
    style: {
      flex: 1,
      overflowY: 'auto',
      minHeight: 0
    }
  }, localMessages.length === 0 ? /*#__PURE__*/React.createElement("div", {
    className: "text-center text-muted mt-4"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "comments",
    size: "2x"
  }), /*#__PURE__*/React.createElement("p", {
    className: "mt-2"
  }, emptyStateMessage)) : localMessages.map((message, index) => /*#__PURE__*/React.createElement(AiChatMessage, {
    key: message.id || index,
    message: message
  })), /*#__PURE__*/React.createElement("div", {
    ref: messagesEndRef
  })), /*#__PURE__*/React.createElement("div", {
    className: "chat-input-container p-3 border-top",
    style: {
      flexShrink: 0
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "input-group"
  }, /*#__PURE__*/React.createElement("textarea", {
    ref: inputRef,
    "data-testid": "t-ai-chat-input",
    value: currentMessage,
    placeholder: placeholder,
    onChange: e => setCurrentMessage(e.target.value),
    className: "form-control t-ai-chat-input tqc-control",
    onKeyDown: handleKeyDown,
    rows: 2,
    disabled: isLoading || localMessages.some(message => message.isWaiting),
    style: {
      resize: 'none'
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: "input-group-append"
  }, currentMessage.trim() && /*#__PURE__*/React.createElement(Button, {
    color: "outline-secondary",
    size: "sm",
    className: "me-2",
    onClick: handleClear,
    disabled: isLoading || localMessages.some(message => message.isWaiting),
    "data-testid": "t-ai-chat-clear"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "times-circle"
  })), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    size: "sm",
    onClick: handleSendMessage,
    disabled: !currentMessage.trim() || isLoading || localMessages.some(message => message.isWaiting),
    "data-testid": "t-ai-chat-send"
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "arrow-up"
  })))))));
};
AiChatInput.propTypes = {
  className: PropTypes.string,
  onClear: PropTypes.func,
  placeholder: PropTypes.string.isRequired,
  text: PropTypes.string,
  messages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    content: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['user', 'assistant', 'system']).isRequired,
    timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    conversationId: PropTypes.string,
    isWaiting: PropTypes.bool
  })),
  onSendMessage: PropTypes.func,
  isLoading: PropTypes.bool,
  waitingMessage: PropTypes.string.isRequired,
  emptyStateMessage: PropTypes.string.isRequired
};
export default AiChatInput;